import { invokeFunction } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import { isSupabaseConfigured } from "@/lib/env";
import {
  canReuseCollect,
  type KpiCollectMeta,
} from "@/features/kpi/collectCache";

/**
 * KPI entry data access. Three-layer behavior so the whole feature runs
 * BEFORE any production migration (owner directive: single migration event
 * at Phase E):
 *
 *  1. Supabase configured + `kpi_entries` exists (post-Phase E production):
 *     the table is the source of truth; RLS is the authz boundary (admins all
 *     sections; managers see mapped Framework departments — Phase F / Q8).
 *  2. Supabase configured but the table DOESN'T exist yet (local dev today -
 *     dev talks to prod Supabase for AUTH, but the KPI table isn't migrated):
 *     queries fail with Postgres 42P01 / PostgREST PGRST205 -> we fall back to
 *     localStorage transparently and remember it (see kpiBackend()) so the UI
 *     can tell the admin their values live in this browser only.
 *  3. No Supabase at all (preview mode): straight to localStorage.
 */

export type KpiPeriodType = "week" | "month";

export type KpiEntrySource = "manual" | "auto";

export interface KpiEntry {
  kpiId: string;
  periodType: KpiPeriodType;
  /** ISO date (YYYY-MM-DD): first of the month, or Monday of the week. */
  periodStart: string;
  value: number;
  note: string;
  /** Provenance: manual (human) or auto (kpi-collect cron). Manual wins. */
  source: KpiEntrySource;
  /** When the collector computed the value (auto entries only). */
  computedAt?: string;
  /** The math behind an auto value ("14 breakdowns ÷ 31 trucks"). */
  detail?: Record<string, unknown> | null;
}

const LS_KEY = "bb_kpi_entries";
const COLLECT_META_KEY = "bb_kpi_last_collect";

const supabaseReady = () => isSupabaseConfigured && !!supabase;

/** Which backend served the LAST kpi read/write - drives the UI notice. */
let lastBackend: "supabase" | "local" = supabaseReady() ? "supabase" : "local";

export function kpiBackend(): "supabase" | "local" {
  return lastBackend;
}

/** Postgres "relation does not exist" / PostgREST "table not in schema cache". */
function isMissingTable(e: unknown): boolean {
  const err = e as { code?: string; message?: string } | null;
  if (!err) return false;
  if (err.code === "42P01" || err.code === "PGRST205") return true;
  return typeof err.message === "string" && err.message.includes("does not exist");
}

// ---------------------------------------------------------------------------
// Period helpers
// ---------------------------------------------------------------------------

function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Period start containing `date`: 1st of month, or Monday of the week. */
export function periodStartFor(date: Date, type: KpiPeriodType): string {
  if (type === "month") return toISODate(new Date(date.getFullYear(), date.getMonth(), 1));
  const d = new Date(date);
  const dow = (d.getDay() + 6) % 7; // Monday = 0
  d.setDate(d.getDate() - dow);
  return toISODate(d);
}

/** Move a period start forward/back by `delta` periods. */
export function shiftPeriod(periodStart: string, type: KpiPeriodType, delta: number): string {
  const [y, m, d] = periodStart.split("-").map(Number);
  if (type === "month") return toISODate(new Date(y, m - 1 + delta, 1));
  return toISODate(new Date(y, m - 1, d + delta * 7));
}

/** First day *after* this period — exclusive end for date-range queries. */
export function periodEndExclusive(periodStart: string, type: KpiPeriodType): string {
  return shiftPeriod(periodStart, type, 1);
}

/** Human label: "August 2026" or "Week of Aug 11, 2026". */
export function periodLabel(periodStart: string, type: KpiPeriodType): string {
  const [y, m, d] = periodStart.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  if (type === "month") {
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  }
  return `Week of ${date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
}

// ---------------------------------------------------------------------------
// Local (localStorage) fallback
// ---------------------------------------------------------------------------

function localReadAll(): KpiEntry[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<KpiEntry>[];
      // Entries saved before the provenance fields existed default to manual.
      return parsed.map((e) => ({
        kpiId: e.kpiId ?? "",
        periodType: e.periodType ?? "month",
        periodStart: e.periodStart ?? "",
        value: Number(e.value ?? 0),
        note: e.note ?? "",
        source: e.source ?? "manual",
        computedAt: e.computedAt,
        detail: e.detail ?? null,
      }));
    }
  } catch {
    /* ignore malformed cache */
  }
  return [];
}

function localWriteAll(entries: KpiEntry[]): void {
  localStorage.setItem(LS_KEY, JSON.stringify(entries));
}

export function readKpiCollectMeta(): KpiCollectMeta | null {
  try {
    const raw = localStorage.getItem(COLLECT_META_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<KpiCollectMeta>;
    if (!parsed.at) return null;
    return {
      at: parsed.at,
      status: parsed.status === "partial" || parsed.status === "failed" ? parsed.status : "success",
      fleetioOk: parsed.fleetioOk === true,
      periodTypes: Array.isArray(parsed.periodTypes)
        ? parsed.periodTypes.filter((item): item is "week" | "month" => item === "week" || item === "month")
        : undefined,
    };
  } catch {
    return null;
  }
}

function writeKpiCollectMeta(meta: KpiCollectMeta) {
  localStorage.setItem(COLLECT_META_KEY, JSON.stringify(meta));
}

function collectModesFor(periodType: KpiPeriodType): string[] {
  return periodType === "week"
    ? ["weekly", "daily", "recompute"]
    : ["monthly", "daily", "recompute"];
}

interface CollectionRunRow {
  mode: string;
  status: string;
  started_at: string;
  finished_at: string | null;
  details: {
    collectors?: Array<{
      collector: string;
      status: string;
      note?: string;
      error?: string;
      values?: number;
    }>;
  } | null;
}

function runToMeta(row: CollectionRunRow, periodType: KpiPeriodType): KpiCollectMeta {
  const collectors = row.details?.collectors ?? [];
  const fleetio = collectors.find((item) => item.collector === "fleetio");
  const fleetioNote = `${fleetio?.note ?? ""} ${fleetio?.error ?? ""}`.toLowerCase();
  const fleetioOk = Boolean(
    fleetio &&
      fleetio.status !== "failed" &&
      fleetio.status !== "skipped" &&
      (fleetio.values ?? 0) > 0 &&
      !fleetioNote.includes("429") &&
      !fleetioNote.includes("work orders unavailable"),
  );
  const status =
    row.status === "failed" || row.status === "partial" ? row.status : "success";
  return {
    at: row.finished_at ?? row.started_at,
    status,
    fleetioOk,
    periodTypes: [periodType],
    shared: true,
  };
}

/** Last collect any admin ran — shared, not this browser. */
export async function readSharedCollectMeta(
  periodType: KpiPeriodType,
): Promise<KpiCollectMeta | null> {
  if (supabaseReady()) {
    const { data, error } = await supabase!
      .from("kpi_collection_runs")
      .select("mode,status,started_at,finished_at,details")
      .in("mode", collectModesFor(periodType))
      .in("status", ["success", "partial", "failed"])
      .order("started_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!error && data) return runToMeta(data as CollectionRunRow, periodType);
    if (error && !isMissingTable(error)) throw error;
  }
  return readKpiCollectMeta();
}

function fleetioPullOk(result: KpiCollectResult): boolean {
  const fleetio = result.collectors.find((item) => item.collector === "fleetio");
  if (!fleetio || fleetio.status === "failed" || fleetio.status === "skipped") return false;
  const note = `${fleetio.note ?? ""} ${fleetio.error ?? ""}`.toLowerCase();
  if (note.includes("work orders unavailable") || note.includes("429")) return false;
  return fleetio.values > 0;
}

async function cachedCollectResult(
  periodType: KpiPeriodType,
  meta: KpiCollectMeta | null,
): Promise<KpiCollectResult> {
  const stored = await listKpiEntries(periodType);
  const entries = stored
    .filter((entry) => entry.source === "auto")
    .map((entry) => ({
      kpiId: entry.kpiId,
      periodType: entry.periodType,
      periodStart: entry.periodStart,
      value: entry.value,
      detail: entry.detail ?? undefined,
      collector: typeof entry.detail?.collector === "string" ? entry.detail.collector : "cache",
    }));
  const pulled = meta
    ? new Date(meta.at).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })
    : "";
  const who = meta?.shared ? " (shared — every admin sees this pull)" : "";
  return {
    status: "success",
    preview: lastBackend === "local",
    inserted: 0,
    updated: 0,
    skippedManual: 0,
    entries,
    fromCache: true,
    collectors: [
      {
        collector: "cache",
        status: "success",
        values: entries.length,
        note: `Showing the last pull from ${pulled}${who}. Live systems were not asked again. Click Refresh from systems only if you need a new pull.`,
      },
    ],
  };
}

function localUpsert(entry: KpiEntry): void {
  const all = localReadAll();
  const idx = all.findIndex(
    (e) =>
      e.kpiId === entry.kpiId &&
      e.periodType === entry.periodType &&
      e.periodStart === entry.periodStart,
  );
  if (idx >= 0) all[idx] = entry;
  else all.push(entry);
  localWriteAll(all);
}

// ---------------------------------------------------------------------------
// Supabase row mapping (select * so the code tolerates schema versions)
// ---------------------------------------------------------------------------

interface KpiEntryRow {
  kpi_id: string;
  period_type: KpiPeriodType;
  period_start: string;
  value: number | string;
  note?: string | null;
  source?: string | null;
  computed_at?: string | null;
  detail?: Record<string, unknown> | null;
}

function rowToEntry(r: KpiEntryRow): KpiEntry {
  return {
    kpiId: r.kpi_id,
    periodType: r.period_type,
    periodStart: r.period_start,
    value: Number(r.value),
    note: r.note ?? "",
    source: r.source === "auto" ? "auto" : "manual",
    computedAt: r.computed_at ?? undefined,
    detail: r.detail ?? null,
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** All entries of one period type (table stays small: ~47 KPIs x periods). */
export async function listKpiEntries(periodType: KpiPeriodType): Promise<KpiEntry[]> {
  if (supabaseReady()) {
    const { data, error } = await supabase!
      .from("kpi_entries")
      .select("*")
      .eq("period_type", periodType)
      .order("period_start", { ascending: false });
    if (!error) {
      lastBackend = "supabase";
      return (data as KpiEntryRow[]).map(rowToEntry);
    }
    if (!isMissingTable(error)) throw error;
    // Table not migrated yet (pre-Phase E) -> browser storage.
  }
  lastBackend = "local";
  return localReadAll().filter((e) => e.periodType === periodType);
}

/**
 * Save a manually entered value. (Auto values are written only by the
 * kpi-collect edge function server-side, never from the browser.)
 */
export async function upsertKpiEntry(
  entry: Omit<KpiEntry, "source" | "computedAt" | "detail">,
  userId?: string,
): Promise<void> {
  const manualEntry: KpiEntry = { ...entry, source: "manual", detail: null };

  if (supabaseReady()) {
    const { error } = await supabase!
      .from("kpi_entries")
      .upsert(
        {
          kpi_id: entry.kpiId,
          period_type: entry.periodType,
          period_start: entry.periodStart,
          value: entry.value,
          note: entry.note,
          source: "manual",
          computed_at: null,
          detail: null,
          entered_by: userId ?? null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "kpi_id,period_type,period_start" },
      );
    if (!error) {
      lastBackend = "supabase";
      return;
    }
    if (!isMissingTable(error)) throw error;
  }
  lastBackend = "local";
  localUpsert(manualEntry);
}

export interface KpiCollectEntry {
  kpiId: string;
  periodType: KpiPeriodType;
  periodStart: string;
  value: number;
  detail?: Record<string, unknown>;
  collector?: string;
}

export interface KpiCollectResult {
  status: string;
  preview: boolean;
  inserted: number;
  updated: number;
  skippedManual: number;
  entries: KpiCollectEntry[];
  fromCache?: boolean;
  collectors: Array<{
    collector: string;
    status: string;
    note?: string;
    error?: string;
    values: number;
  }>;
}

/**
 * Apply collector output to browser storage. Manual rows are never replaced.
 * Used before Phase E, when kpi_entries does not exist yet.
 */
export function applyAutoKpiEntries(incoming: KpiCollectEntry[]): {
  applied: number;
  skippedManual: number;
} {
  const all = localReadAll();
  let applied = 0;
  let skippedManual = 0;
  const computedAt = new Date().toISOString();

  for (const item of incoming) {
    const idx = all.findIndex(
      (entry) =>
        entry.kpiId === item.kpiId &&
        entry.periodType === item.periodType &&
        entry.periodStart === item.periodStart,
    );
    if (idx >= 0 && all[idx].source === "manual") {
      skippedManual += 1;
      continue;
    }
    const next: KpiEntry = {
      kpiId: item.kpiId,
      periodType: item.periodType,
      periodStart: item.periodStart,
      value: item.value,
      note: "",
      source: "auto",
      computedAt,
      detail: { ...item.detail, collector: item.collector },
    };
    if (idx >= 0) all[idx] = next;
    else all.push(next);
    applied += 1;
  }

  localWriteAll(all);
  lastBackend = "local";
  return { applied, skippedManual };
}

/** Ask the server to compute automatic KPIs from Fleetio / Samsara / QuickManage / FMCSA. */
export async function collectKpiValues(
  _mode: "daily" | "weekly" | "monthly" | "recompute" = "recompute",
  periodType: KpiPeriodType = "month",
  options: { force?: boolean } = {},
): Promise<KpiCollectResult> {
  const shared = await readSharedCollectMeta(periodType);
  if (!options.force && canReuseCollect(shared, Date.now(), periodType)) {
    writeKpiCollectMeta(shared!);
    return cachedCollectResult(periodType, shared);
  }

  const collectMode = periodType === "week" ? "weekly" : "monthly";
  try {
    const result = await invokeFunction<KpiCollectResult>("kpi-collect", {
      mode: collectMode,
      bypassCache: options.force === true,
    });
    if (result.fromCache) {
      const meta = (await readSharedCollectMeta(periodType)) ?? shared;
      if (meta) writeKpiCollectMeta(meta);
      return cachedCollectResult(periodType, meta ?? null);
    }
    if (result.preview || lastBackend === "local") {
      applyAutoKpiEntries(result.entries ?? []);
    }
    const meta: KpiCollectMeta = {
      at: new Date().toISOString(),
      status: result.status === "failed" ? "failed" : result.status === "partial" ? "partial" : "success",
      fleetioOk: fleetioPullOk(result),
      periodTypes: [periodType],
      shared: result.preview !== true,
    };
    writeKpiCollectMeta(meta);
    return result;
  } catch (error) {
    const { collectFleetSnapshot } = await import("@/features/kpi/livePreview");
    const entries = await collectFleetSnapshot(periodType);
    const applied = applyAutoKpiEntries(entries);
    const result: KpiCollectResult = {
      status: "partial",
      preview: true,
      inserted: applied.applied,
      updated: 0,
      skippedManual: applied.skippedManual,
      entries,
      collectors: [
        {
          collector: "fleet-snapshot",
          status: entries.length ? "success" : "skipped",
          values: entries.length,
          note: "kpi-collect is not reachable. Showing current Fleetio availability and PM snapshot.",
          error: error instanceof Error ? error.message : undefined,
        },
      ],
    };
    writeKpiCollectMeta({
      at: new Date().toISOString(),
      status: "partial",
      fleetioOk: false,
      periodTypes: [periodType],
    });
    return result;
  }
}

export async function deleteKpiEntry(
  kpiId: string,
  periodType: KpiPeriodType,
  periodStart: string,
): Promise<void> {
  if (supabaseReady()) {
    const { error } = await supabase!
      .from("kpi_entries")
      .delete()
      .eq("kpi_id", kpiId)
      .eq("period_type", periodType)
      .eq("period_start", periodStart);
    if (!error) {
      lastBackend = "supabase";
      return;
    }
    if (!isMissingTable(error)) throw error;
  }
  lastBackend = "local";
  localWriteAll(
    localReadAll().filter(
      (e) =>
        !(e.kpiId === kpiId && e.periodType === periodType && e.periodStart === periodStart),
    ),
  );
}
